import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Environment } from '../src/app/website/services/environment.service';
import { TenantConfig, TenantLogos } from './tenant-config.model';
import { DEFAULT_TENANT_CONFIG, TENANT_CONFIGS } from './tenant-configs';

/** Assets used when neither the DB nor a brand map provides one (MAB fallback). */
const DEFAULT_LOGO_URL = 'assets/brand/logos/MAB-light.png';
const DEFAULT_DARK_LOGO_URL = 'assets/brand/logos/MAB-dark.svg';
const DEFAULT_MOBILE_ICON_URL = 'assets/brand/logos/MAB-mobile-light.png';
const DEFAULT_LOGIN_LOGO_URL = 'assets/brand/logos/MAB-full-light.png';
const DEFAULT_ADVISOR_LOGIN_BACKGROUND_URL = 'assets/brand/img/mab-advisor-login-background.jpg';
const DEFAULT_CLIENT_LOGIN_BACKGROUND_URL = 'assets/brand/img/garden-steps.jpg';
/** Favicon used when the DB provides none and the brand is mab or the AR fallback. */
const MAB_FAVICON_URL = 'assets/brand/logos/favicon-mab.ico';
const DEFAULT_APPLE_TOUCH_ICON_URL = 'assets/icons/apple-touch-mab-icon.png';
const DEFAULT_THEME_COLOR = '#242f3a';
const DEFAULT_BACKGROUND_COLOR = '#ffffff';

interface ManifestIcon {
  src: string;
  sizes: string;
  type: string;
  purpose: string;
}

const DEFAULT_MANIFEST_ICONS: ManifestIcon[] = [
  {
    src: 'assets/icons/apple-touch-mab-icon.png',
    sizes: '180x180',
    type: 'image/png',
    purpose: 'any'
  },
  {
    src: 'assets/icons/favicon-mab-32x32.png',
    sizes: '32x32',
    type: 'image/png',
    purpose: 'any'
  },
  {
    src: 'assets/icons/favicon-mab-16x16.png',
    sizes: '16x16',
    type: 'image/png',
    purpose: 'any'
  }
];

const BRAND_FALLBACK_APPLE_TOUCH_ICONS: { [brand: string]: string } = {
  heron: 'assets/icons/pwa/everglades-icon-512.png'
};

const BRAND_FALLBACK_MANIFEST_ICONS: { [brand: string]: ManifestIcon[] } = {
  heron: [
    {
      src: 'assets/icons/pwa/everglades-icon.svg',
      sizes: 'any',
      type: 'image/svg+xml',
      purpose: 'any'
    },
    {
      src: 'assets/icons/pwa/everglades-icon-192.png',
      sizes: '192x192',
      type: 'image/png',
      purpose: 'any'
    },
    {
      src: 'assets/icons/pwa/everglades-icon-512.png',
      sizes: '512x512',
      type: 'image/png',
      purpose: 'any'
    },
    {
      src: 'assets/icons/pwa/everglades-icon-192-maskable.png',
      sizes: '192x192',
      type: 'image/png',
      purpose: 'maskable'
    },
    {
      src: 'assets/icons/pwa/everglades-icon-512-maskable.png',
      sizes: '512x512',
      type: 'image/png',
      purpose: 'maskable'
    }
  ]
};

/**
 * Local logo assets per brand, used only while the tenant-config API
 * doesn't return branding.logos — the DB value always wins once present.
 * Brands absent here (e.g. mab, citrus) fall through to the MAB defaults.
 */
const BRAND_FALLBACK_LOGOS: { [brand: string]: string } = {
  heron: 'assets/brand/logos/Everglades-light-bg_fg.svg'
};

/**
 * Local logo assets per brand for dark backgrounds, used only while the
 * tenant-config API doesn't return branding.logos.desktopDarkMode — the DB
 * value always wins once present. Brands absent here (e.g. mab, citrus) fall
 * through to the MAB dark default.
 */
const BRAND_FALLBACK_DARK_LOGOS: { [brand: string]: string } = {
  heron: 'assets/brand/logos/Everglades-dark-bg.svg'
};

/**
 * Local compact icon assets per brand (mobile/small viewports), used only
 * while the tenant-config API doesn't return branding.logos.mobileIcon.
 * Brands absent here (e.g. mab, citrus) fall through to the MAB default.
 */
const BRAND_FALLBACK_MOBILE_ICONS: { [brand: string]: string } = {
  heron: 'assets/brand/logos/Everglades-E.svg'
};

/**
 * Local login-page assets per brand, used only while the tenant-config API
 * doesn't return branding.logos.loginLogo / branding.advisorLoginBackground /
 * branding.clientLoginBackground. Brands absent here (e.g. citrus, no
 * tenant) fall through to the MAB / garden-steps defaults.
 */
const BRAND_FALLBACK_LOGIN: {
  [brand: string]: { loginLogo?: string; advisorLoginBackground?: string; clientLoginBackground?: string };
} = {
  heron: {
    loginLogo: 'assets/brand/logos/Everglades-light-bg_fg.svg',
    advisorLoginBackground: 'assets/brand/img/sign-in-family.jpg'
  },
  mab: {
    clientLoginBackground: 'assets/brand/img/mab-client-login-background.webp'
  }
};

/**
 * Identity strings per brand, used only while the tenant-config API
 * doesn't return productName/companyName. Brands absent here (no tenant)
 * fall through to the MAB defaults.
 */
const BRAND_FALLBACK_IDENTITY: { [brand: string]: { productName: string; companyName: string } } = {
  heron: { productName: 'Everglades', companyName: 'Heron Financial' },
  mab: { productName: 'Mortgage Advice Bureau', companyName: 'Mortgage Advice Bureau' },
  citrus: { productName: 'Citrus', companyName: 'Citrus' }
};
const DEFAULT_IDENTITY = { productName: 'Mortgage Advice Bureau', companyName: 'Mortgage Advice Bureau' };

/**
 * Privacy-policy URL per brand, used only while the tenant-config API
 * doesn't return privacyPolicyUrl — the DB value always wins once present.
 * Brands absent here get no URL and the UI hides the link; never invent one.
 */
const BRAND_FALLBACK_PRIVACY_POLICY_URLS: { [brand: string]: string } = {
  heron: 'https://heronfinancial.co.uk/privacy-policy/'
};

/**
 * Loads the database-backed tenant configuration (brand id, logos and
 * feature flags) and applies it to the document.
 *
 * How it interacts with the compiled styles:
 * - styles/tokens/_semantic.scss declares the AR fallback for every --ds-*
 *   token under :root; brand colours are NOT fetched — they are compiled in.
 * - config.brand sets [data-brand] on <html>, activating the matching
 *   styles/brands/_<id>.scss partial; absent id means the AR fallback.
 * - Logo URLs returned by the DB are exposed via `logos`/`logoUrl` and the
 *   favicon is applied to <head>.
 */
@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private readonly API_NAME = 'tenantConfig';
  private readonly METHOD_NAME = 'getTenantConfig';

  private readonly configSubject = new BehaviorSubject<TenantConfig>(DEFAULT_TENANT_CONFIG);
  public readonly config$: Observable<TenantConfig> = this.configSubject.asObservable();

  constructor(
    private http: HttpClient,
    // Each app provides its own concrete Environment (Everglades/Client),
    // so the API origin always comes from the app-correct environment file.
    private env: Environment,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  public get config(): TenantConfig {
    return this.configSubject.getValue();
  }

  public isFeatureEnabled(flag: string): boolean {
    return !!this.config.featureFlags?.[flag];
  }

  /**
   * Resolves a branded asset URL for the current tenant: Heron keeps the
   * given URL; every other brand swaps in the "-default" variant
   * (X.svg -> X-default.svg).
   */
  public brandAssetUrl(url: string): string {
    if (!url) {
      return url;
    }
    return this.config.brandId === 'heron' ? url : url.replace(/\.svg$/, '-default.svg');
  }

  /**
   * Primary logo to render (desktop light mode): DB
   * branding.logos.desktopLightMode wins, else a per-brand local asset,
   * else the MAB default. Always defined.
   */
  public get desktopLogoUrl(): string {
    return (
      this.config.branding?.logos?.desktopLightMode ??
      BRAND_FALLBACK_LOGOS[this.config.brandId ?? ''] ??
      DEFAULT_LOGO_URL
    );
  }

  /**
   * Primary logo to render on dark backgrounds (desktop): DB
   * branding.logos.desktopDarkMode wins, else a per-brand local asset,
   * else the MAB dark default. Always defined.
   */
  public get desktopDarkLogoUrl(): string {
    return (
      this.config.branding?.logos?.desktopDarkMode ??
      BRAND_FALLBACK_DARK_LOGOS[this.config.brandId ?? ''] ??
      DEFAULT_DARK_LOGO_URL
    );
  }

  /**
   * Logo rendered on the login page: DB branding.logos.loginLogo wins,
   * else a per-brand local asset, else the MAB default. Always defined.
   */
  public get loginLogoUrl(): string {
    return (
      this.config.branding?.logos?.loginLogo ??
      BRAND_FALLBACK_LOGIN[this.config.brandId ?? '']?.loginLogo ??
      DEFAULT_LOGIN_LOGO_URL
    );
  }

  /**
   * Compact square icon for mobile/small viewports: DB
   * branding.logos.mobileIcon wins, else a per-brand local asset,
   * else the MAB default. Always defined.
   */
  public get mobileIconUrl(): string {
    return (
      this.config.branding?.logos?.mobileIcon ??
      BRAND_FALLBACK_MOBILE_ICONS[this.config.brandId ?? ''] ??
      DEFAULT_MOBILE_ICON_URL
    );
  }

  /**
   * Background image for the advisor login page: DB
   * branding.advisorLoginBackground wins, else a per-brand local asset,
   * else the MAB default. Always defined.
   */
  public get advisorLoginBackgroundUrl(): string {
    return (
      this.config.branding?.advisorLoginBackground ??
      BRAND_FALLBACK_LOGIN[this.config.brandId ?? '']?.advisorLoginBackground ??
      DEFAULT_ADVISOR_LOGIN_BACKGROUND_URL
    );
  }

  /**
   * Background image for the client login page: DB
   * branding.clientLoginBackground wins, else a per-brand local asset,
   * else the garden-steps default. Always defined.
   */
  public get clientLoginBackgroundUrl(): string {
    return (
      this.config.branding?.clientLoginBackground ??
      BRAND_FALLBACK_LOGIN[this.config.brandId ?? '']?.clientLoginBackground ??
      DEFAULT_CLIENT_LOGIN_BACKGROUND_URL
    );
  }

  /**
   * Privacy-policy URL for the current tenant: DB privacyPolicyUrl wins,
   * else a per-brand fallback. Undefined when neither provides one —
   * callers should hide the link rather than guess a URL.
   */
  public get privacyPolicyUrl(): string | undefined {
    return this.config.privacyPolicyUrl ?? BRAND_FALLBACK_PRIVACY_POLICY_URLS[this.config.brandId ?? ''];
  }

  /**
   * 'Optional Contact Updates Defaults' tenant setting: when true, the
   * non-essential contact-update preferences are pre-ticked in the client
   * account setup wizard; absent/false leaves them off.
   */
  public get optionalContactUpdatesDefaults(): boolean {
    return !!this.config.optionalContactUpdatesDefaults;
  }

  /**
   * Label for the compliance handover workflow: 'Compliance Handover' for
   * Heron, 'Case Handover' for every other tenant (MAB wording).
   */
  public get complianceHandoverName(): string {
    return this.config.brandId === 'heron' ? 'Compliance Handover' : 'Case Handover';
  }

  /**
   * Label for the compliance approval step: 'Compliance Approval' for
   * Heron, 'Case Handover Approval' for every other tenant (MAB wording).
   */
  public get complianceApprovalName(): string {
    return this.config.brandId === 'heron' ? 'Compliance Approval' : 'Case Handover Approval';
  }

  /**
   * All logo URLs returned by the tenant config (desktop light/dark,
   * mobile icon, favicon and the alternative variants). Empty when the
   * API provided none.
   */
  public get logos(): TenantLogos {
    return this.config.branding?.logos ?? {};
  }

  /**
   * Fetches the tenant config from this tenant's API and applies it.
   * Called from APP_INITIALIZER so branding lands before first paint.
   * Never rejects: a failed request degrades to the AR fallback.
   */
  public load(): Observable<TenantConfig> {
    // Dev override: ?tenantConfig=<key> applies a TENANT_CONFIGS fixture
    // and skips the API call entirely while the endpoint doesn't exist.
    const devKey = new URLSearchParams(this.doc.location.search).get('tenantConfig');
    if (devKey) {
      const config = TENANT_CONFIGS[devKey];
      if (config) {
        this.applyConfig(config);
      } else {
        console.warn(`TenantConfigService: unknown ?tenantConfig="${devKey}", using AR fallback.`);
        this.applyConfig(DEFAULT_TENANT_CONFIG);
      }
      return of(this.config);
    }

    const url = `${this.env.rawApiUrl}${this.API_NAME}${this.env.apiVersion}${this.METHOD_NAME}`;
    return this.http.get<TenantConfig>(url, { withCredentials: true }).pipe(
      tap((config) => this.applyConfig(this.withRouteDefaults(config))),
      catchError((error) => {
        console.warn('TenantConfigService: config request failed, using tenant/AR fallback.', error);
        const config = this.withRouteDefaults();
        this.applyConfig(config);
        return of(config);
      })
    );
  }

  /**
   * Merges the API response over the static TENANT_CONFIGS entry for the
   * route's tenantId (if any) and falls back to the route's brandId.
   * Priority: API response -> TENANT_CONFIGS[env.tenantId] -> route brandId -> DEFAULT_TENANT_CONFIG.
   */
  private withRouteDefaults(apiConfig?: TenantConfig | null): TenantConfig {
    const tenantFallback = (this.env.tenantId && TENANT_CONFIGS[this.env.tenantId]) || DEFAULT_TENANT_CONFIG;
    const baseBrandId = tenantFallback.brandId || this.env.brandId || this.env.tenantId || '';

    if (!apiConfig) {
      return {
        ...tenantFallback,
        brandId: baseBrandId
      };
    }

    return {
      ...tenantFallback,
      ...apiConfig,
      brandId: apiConfig.brandId || baseBrandId,
      productName: apiConfig.productName || tenantFallback.productName,
      companyName: apiConfig.companyName || tenantFallback.companyName,
      privacyPolicyUrl: apiConfig.privacyPolicyUrl ?? tenantFallback.privacyPolicyUrl,
      branding: {
        ...tenantFallback.branding,
        ...apiConfig.branding,
        logos: {
          ...tenantFallback.branding?.logos,
          ...apiConfig.branding?.logos
        }
      },
      featureFlags: {
        ...tenantFallback.featureFlags,
        ...apiConfig.featureFlags
      }
    };
  }

  /**
   * Dev-only brand/tenant preview: applies the matching TENANT_CONFIGS fixture
   * (or the AR fallback for 'ar'/anything else). The fixture's `brandId` field
   * drives [data-brand] on <html> via applyConfig.
   */
  public previewBrand(key: string): void {
    if (TENANT_CONFIGS[key]) {
      const fixture = TENANT_CONFIGS[key];
      this.applyConfig({ ...fixture, brandId: fixture.brandId || key });
    } else {
      this.applyConfig(DEFAULT_TENANT_CONFIG);
    }
  }

  /** Re-applies branding and config state (e.g. after a tenant switch/reset). */
  public applyConfig(config: TenantConfig): void {
    config = this.withIdentityFallbacks(config);
    this.configSubject.next(config);
    // [data-brand] activates the generated styles/brands/_<id>.scss partial;
    // absent id means the AR :root fallback applies.
    if (config?.brandId) {
      this.root.setAttribute('data-brand', config.brandId);
    } else {
      this.root.removeAttribute('data-brand');
    }
    this.applyFavicon(config);
    this.applyDocumentMeta(config);
  }

  private get root(): HTMLElement {
    return this.doc.documentElement;
  }

  /**
   * Fills productName/companyName from the per-brand identity fallbacks
   * while the API omits them, so templates always render a real name.
   */
  private withIdentityFallbacks(config: TenantConfig): TenantConfig {
    const identity = BRAND_FALLBACK_IDENTITY[config?.brandId ?? ''] ?? DEFAULT_IDENTITY;
    return {
      ...config,
      productName: config?.productName || identity.productName,
      companyName: config?.companyName || identity.companyName
    };
  }

  /**
   * Points <link rel="icon"> and <link rel="apple-touch-icon"> at the
   * resolved brand icons.
   */
  private applyFavicon(config?: TenantConfig): void {
    const favicon =
      config?.branding?.logos?.favicon ?? (!config?.brandId || config.brandId === 'mab' ? MAB_FAVICON_URL : undefined);
    if (favicon) {
      let link = this.doc.querySelector<HTMLLinkElement>('link[rel~="icon"]');
      if (!link) {
        link = this.doc.createElement('link');
        link.rel = 'icon';
        this.doc.head.appendChild(link);
      }
      link.href = favicon;
    }

    const appleTouchLink = this.doc.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (appleTouchLink) {
      appleTouchLink.href = BRAND_FALLBACK_APPLE_TOUCH_ICONS[config?.brandId ?? ''] ?? DEFAULT_APPLE_TOUCH_ICON_URL;
    }
  }

  /**
   * Updates <title>, meta[name="description"], meta[name="theme-color"], and
   * <link rel="manifest"] with the resolved tenant identity and brand tokens.
   */
  private applyDocumentMeta(config: TenantConfig): void {
    this.doc.title = config.productName;

    const description = this.doc.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      description.content = config.companyName;
    }

    const themeColor = this.resolveCssToken('--ds-bg-brand-solid', DEFAULT_THEME_COLOR);
    const backgroundColor = this.resolveCssToken('--ds-bg-primary', DEFAULT_BACKGROUND_COLOR);

    const themeColorMeta = this.doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.content = themeColor;
    }

    this.applyManifest(config, themeColor, backgroundColor);
  }

  private applyManifest(config: TenantConfig, themeColor: string, backgroundColor: string): void {
    const manifestLink = this.doc.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!manifestLink) {
      return;
    }

    const baseUri = this.doc.baseURI || this.doc.location.origin;
    const resolveUrl = (path: string): string => new URL(path, baseUri).href;
    const icons = BRAND_FALLBACK_MANIFEST_ICONS[config.brandId ?? ''] ?? DEFAULT_MANIFEST_ICONS;
    const fullName =
      config.companyName && config.companyName !== config.productName
        ? `${config.productName} by ${config.companyName}`
        : config.productName;

    const manifest = {
      name: fullName,
      short_name: config.productName,
      theme_color: themeColor,
      background_color: backgroundColor,
      display: 'standalone',
      scope: resolveUrl('/'),
      start_url: resolveUrl('/'),
      icons: icons.map((icon) => ({
        ...icon,
        src: resolveUrl(icon.src)
      }))
    };

    manifestLink.href = `data:application/manifest+json;charset=utf-8,${encodeURIComponent(JSON.stringify(manifest))}`;
  }

  private resolveCssToken(token: string, fallback: string): string {
    return this.doc.defaultView?.getComputedStyle(this.root).getPropertyValue(token).trim() || fallback;
  }
}
